import { integrationApi } from '@/api/integrations';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  ShoppingBagIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  padding: 2rem;
  max-width: 1560px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem 0.625rem 2.5rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: var(--gray-400);
`;

const FilterSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--primary-700);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const StatIconWrapper = styled.div<{ $color: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$color};

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const MarketplacesGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const MarketplaceCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
  }
`;

const MarketplaceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const MarketplaceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MarketplaceLogo = styled.div<{ $color: string }>`
  width: 4rem;
  height: 4rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$color};

  svg {
    width: 2.5rem;
    height: 2.5rem;
    color: white;
  }
`;

const MarketplaceDetails = styled.div``;

const MarketplaceName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.25rem;
`;

const MarketplaceType = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const MarketplaceActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${(props) =>
    props.$status === 'active'
      ? 'var(--success-100)'
      : props.$status === 'error'
        ? 'var(--error-100)'
        : props.$status === 'pending'
          ? 'var(--blue-100)'
          : 'var(--gray-100)'};
  color: ${(props) =>
    props.$status === 'active'
      ? 'var(--success-700)'
      : props.$status === 'error'
        ? 'var(--error-700)'
        : props.$status === 'pending'
          ? 'var(--blue-700)'
          : 'var(--gray-700)'};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid var(--gray-300);
  background: white;
  color: var(--gray-700);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const SyncButton = styled(ActionButton)`
  background: var(--primary-600);
  color: white;
  border-color: var(--primary-600);

  &:hover {
    background: var(--primary-700);
    border-color: var(--primary-700);
  }
`;

const MarketplaceStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  padding: 1.5rem;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
`;

const Stat = styled.div``;

const StatTitle = styled.div`
  font-size: 0.75rem;
  color: var(--gray-600);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const PublishedSection = styled.div`
  margin-top: 2rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const TableFilters = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
`;

const Th = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gray-500);
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--gray-100);
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--gray-50);
  }
`;

const Td = styled.td`
  padding: 0.875rem 1rem;
  font-size: 0.875rem;
  color: var(--gray-700);
  vertical-align: middle;
`;

const ProductNameCell = styled.div`
  font-weight: 500;
  color: var(--gray-900);
`;

const ProductPriceCell = styled.div`
  font-weight: 600;
  color: var(--gray-900);
  white-space: nowrap;
`;

const MarketplaceBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
  background: ${(props) => props.$color};
  white-space: nowrap;
`;

const ProductStatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) =>
    props.$status === 'published'
      ? 'var(--success-100)'
      : props.$status === 'pending'
        ? 'var(--blue-100)'
        : props.$status === 'error'
          ? 'var(--error-100)'
          : 'var(--gray-100)'};
  color: ${(props) =>
    props.$status === 'published'
      ? 'var(--success-700)'
      : props.$status === 'pending'
        ? 'var(--blue-700)'
        : props.$status === 'error'
          ? 'var(--error-700)'
          : 'var(--gray-700)'};
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid var(--gray-200);
  border-radius: 0.375rem;
  background: white;
  color: var(--gray-500);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }

  &:hover {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
  }
`;

const EmptyTableRow = styled.tr``;

const EmptyTableCell = styled.td`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--gray-400);
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-top: 1rem;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  font-size: 0.875rem;
  color: var(--gray-400);
  margin-bottom: 1.5rem;
  max-width: 360px;
`;

const EmptyStateLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.625rem 1.25rem;
  background: var(--primary-600);
  color: white;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--primary-700);
  }
`;

const ViewButton = styled(ActionButton)`
  background: var(--gray-900);
  color: white;
  border-color: var(--gray-900);

  &:hover {
    background: var(--gray-700);
    border-color: var(--gray-700);
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0;
  background: none;
  border: none;
  color: var(--gray-500);
  font-size: 0.875rem;
  cursor: pointer;
  width: fit-content;
  transition: color 0.15s;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: var(--gray-900);
  }
`;

const DetailHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const DetailInfo = styled.div`
  flex: 1;
`;

const DetailMarketplaceLogo = styled.div<{ $color: string }>`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$color};
  flex-shrink: 0;

  svg {
    width: 2rem;
    height: 2rem;
    color: white;
  }
`;

const DetailMarketplaceName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.125rem;
`;

const DetailMarketplaceType = styled.div`
  font-size: 0.875rem;
  color: var(--gray-500);
`;

const DetailStatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailStatCard = styled.div`
  background: white;
  border-radius: 0.625rem;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const DetailStatLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gray-500);
  margin-bottom: 0.5rem;
`;

const DetailStatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const SkuCell = styled.span`
  font-family: monospace;
  font-size: 0.8rem;
  color: var(--gray-500);
`;

const SyncActionButton = styled(ActionButton)`
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalPanel = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-400);
  padding: 0.25rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  transition: color 0.15s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    color: var(--gray-700);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
  overflow-y: auto;
  max-height: 70vh;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
`;

const FormInput = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const FormSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const FormToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormToggleLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
`;

const FormToggleDesc = styled.div`
  font-size: 0.75rem;
  color: var(--gray-400);
  margin-top: 0.125rem;
`;

const Toggle = styled.input`
  width: 2.75rem;
  height: 1.5rem;
  cursor: pointer;
  accent-color: var(--primary-600);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.125rem 1.5rem;
  border-top: 1px solid var(--gray-200);
`;

const ModalCancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  background: white;
  color: var(--gray-700);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--gray-50);
  }
`;

const ModalSaveButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid var(--primary-600);
  border-radius: 0.5rem;
  background: var(--primary-600);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--primary-700);
  }
`;

interface Product {
  id: string;
  name: string;
  sku?: string;
  price: number;
  status: 'published' | 'pending' | 'draft' | 'error';
  lastSync?: string;
}

interface Marketplace {
  id: string;
  slug: string;
  name: string;
  type: string;
  logo: string;
  color: string;
  status: 'active' | 'inactive' | 'error' | 'pending';
  totalProducts: number;
  activeListings: number;
  totalSales: number;
  revenue: number;
  lastSync?: string;
  autoSync: boolean;
  products: Product[];
}

interface PublishedProduct extends Product {
  marketplaceName: string;
  marketplaceColor: string;
  marketplaceId: string;
}

const Marketplaces: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [productMarketplaceFilter, setProductMarketplaceFilter] =
    useState('all');
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [selectedMarketplace, setSelectedMarketplace] =
    useState<Marketplace | null>(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailStatusFilter, setDetailStatusFilter] = useState('all');
  const [configuringMarketplace, setConfiguringMarketplace] =
    useState<Marketplace | null>(null);
  const [configForm, setConfigForm] = useState({
    name: '',
    apiKey: '',
    apiSecret: '',
    region: '',
    autoSync: false,
    syncInterval: '30',
  });
  const [connectedSlugs, setConnectedSlugs] = useState<Set<string> | null>(
    null
  );

  useEffect(() => {
    integrationApi
      .listAll('marketplace')
      .then((groups) => {
        const slugs = new Set<string>();
        groups.forEach((g) =>
          g.integrations.forEach((m) => {
            if (m.is_connected) slugs.add(m.slug);
          })
        );
        setConnectedSlugs(slugs);
      })
      .catch(() => setConnectedSlugs(new Set()));
  }, []);

  // Sample marketplace data
  const marketplaces: Marketplace[] = [
    {
      id: '1',
      slug: 'amazon',
      name: 'Amazon US Store',
      type: 'Amazon',
      logo: 'amazon',
      color: '#FF9900',
      status: 'active',
      totalProducts: 156,
      activeListings: 142,
      totalSales: 3847,
      revenue: 284560,
      lastSync: '5 minutes ago',
      autoSync: true,
      products: [
        {
          id: '1',
          name: 'Premium Wireless Headphones',
          price: 129.99,
          status: 'published',
        },
        {
          id: '2',
          name: 'Smart Watch Series 5',
          price: 349.99,
          status: 'published',
        },
        {
          id: '3',
          name: 'Portable Bluetooth Speaker',
          price: 79.99,
          status: 'published',
        },
        {
          id: '4',
          name: 'USB-C Charging Cable',
          price: 19.99,
          status: 'published',
        },
        {
          id: '5',
          name: 'Wireless Mouse Pro',
          price: 49.99,
          status: 'pending',
        },
      ],
    },
    {
      id: '2',
      slug: 'ebay',
      name: 'eBay Global',
      type: 'eBay',
      logo: 'ebay',
      color: '#E53238',
      status: 'active',
      totalProducts: 98,
      activeListings: 89,
      totalSales: 1532,
      revenue: 98420,
      lastSync: '2 hours ago',
      autoSync: true,
      products: [
        {
          id: '6',
          name: 'Vintage Leather Bag',
          price: 159.99,
          status: 'published',
        },
        {
          id: '7',
          name: 'Professional Camera Lens',
          price: 599.99,
          status: 'published',
        },
        {
          id: '8',
          name: 'Gaming Keyboard RGB',
          price: 89.99,
          status: 'published',
        },
        {
          id: '9',
          name: 'Office Desk Lamp',
          price: 44.99,
          status: 'published',
        },
      ],
    },
    {
      id: '3',
      slug: 'shopify',
      name: 'Shopify Store',
      type: 'Shopify',
      logo: 'shopify',
      color: '#96BF48',
      status: 'active',
      totalProducts: 234,
      activeListings: 220,
      totalSales: 5621,
      revenue: 456780,
      lastSync: '10 minutes ago',
      autoSync: true,
      products: [
        {
          id: '10',
          name: 'Organic Cotton T-Shirt',
          price: 29.99,
          status: 'published',
        },
        {
          id: '11',
          name: 'Eco-Friendly Water Bottle',
          price: 24.99,
          status: 'published',
        },
        {
          id: '12',
          name: 'Yoga Mat Premium',
          price: 54.99,
          status: 'published',
        },
        {
          id: '13',
          name: 'Fitness Resistance Bands',
          price: 19.99,
          status: 'published',
        },
        {
          id: '14',
          name: 'Meditation Cushion',
          price: 39.99,
          status: 'published',
        },
        {
          id: '15',
          name: 'Protein Shaker Bottle',
          price: 14.99,
          status: 'pending',
        },
      ],
    },
    {
      id: '4',
      slug: 'instagram',
      name: 'Instagram Shopping',
      type: 'Instagram',
      logo: 'instagram',
      color: '#E4405F',
      status: 'pending',
      totalProducts: 45,
      activeListings: 0,
      totalSales: 0,
      revenue: 0,
      lastSync: 'Never',
      autoSync: false,
      products: [
        { id: '16', name: 'Fashion Sunglasses', price: 89.99, status: 'draft' },
        { id: '17', name: 'Designer Handbag', price: 299.99, status: 'draft' },
        { id: '18', name: 'Luxury Watch', price: 599.99, status: 'draft' },
      ],
    },
    {
      id: '5',
      slug: 'woocommerce',
      name: 'WooCommerce Store',
      type: 'WooCommerce',
      logo: 'woocommerce',
      color: '#7C3AED',
      status: 'active',
      totalProducts: 187,
      activeListings: 176,
      totalSales: 2934,
      revenue: 198650,
      lastSync: '1 hour ago',
      autoSync: true,
      products: [
        {
          id: '19',
          name: 'Home Decor Vase',
          price: 64.99,
          status: 'published',
        },
        {
          id: '20',
          name: 'Scented Candle Set',
          price: 34.99,
          status: 'published',
        },
        { id: '21', name: 'Wall Art Print', price: 49.99, status: 'published' },
        {
          id: '22',
          name: 'Throw Pillow Cover',
          price: 24.99,
          status: 'published',
        },
      ],
    },
    {
      id: '6',
      slug: 'facebook',
      name: 'Facebook Marketplace',
      type: 'Facebook',
      logo: 'facebook',
      color: '#1877F2',
      status: 'error',
      totalProducts: 67,
      activeListings: 0,
      totalSales: 451,
      revenue: 32180,
      lastSync: '3 days ago',
      autoSync: false,
      products: [
        {
          id: '23',
          name: 'Kitchen Utensil Set',
          price: 39.99,
          status: 'error',
        },
        {
          id: '24',
          name: 'Cutting Board Bamboo',
          price: 29.99,
          status: 'error',
        },
      ],
    },
    {
      id: '7',
      slug: 'magento2',
      name: 'Magento 2 Store',
      type: 'Magento 2',
      logo: 'magento2',
      color: '#F26322',
      status: 'active',
      totalProducts: 0,
      activeListings: 0,
      totalSales: 0,
      revenue: 0,
      lastSync: 'Never',
      autoSync: false,
      products: [],
    },
  ];

  const filteredMarketplaces = marketplaces.filter((mp) => {
    if (connectedSlugs !== null && !connectedSlugs.has(mp.slug)) return false;
    const matchesSearch =
      mp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mp.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || mp.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const publishedProducts: PublishedProduct[] = marketplaces.flatMap((mp) =>
    mp.products.map((p) => ({
      ...p,
      marketplaceName: mp.name,
      marketplaceColor: mp.color,
      marketplaceId: mp.id,
      lastSync: mp.lastSync,
    }))
  );

  const filteredProducts = publishedProducts.filter((p) => {
    const matchesSearch = p.name
      .toLowerCase()
      .includes(productSearch.toLowerCase());
    const matchesMarketplace =
      productMarketplaceFilter === 'all' ||
      p.marketplaceId === productMarketplaceFilter;
    const matchesStatus =
      productStatusFilter === 'all' || p.status === productStatusFilter;
    return matchesSearch && matchesMarketplace && matchesStatus;
  });

  const stats = {
    totalMarketplaces: marketplaces.length,
    activeMarketplaces: marketplaces.filter((mp) => mp.status === 'active')
      .length,
    totalProducts: marketplaces.reduce((sum, mp) => sum + mp.totalProducts, 0),
    totalRevenue: marketplaces.reduce((sum, mp) => sum + mp.revenue, 0),
  };

  const openConfigure = (mp: Marketplace) => {
    setConfiguringMarketplace(mp);
    setConfigForm({
      name: mp.name,
      apiKey: '••••••••••••••••',
      apiSecret: '••••••••••••••••',
      region: mp.type === 'Amazon' ? 'us-east-1' : '',
      autoSync: mp.autoSync,
      syncInterval: '30',
    });
  };

  const closeConfigure = () => setConfiguringMarketplace(null);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon />;
      case 'error':
        return <ExclamationCircleIcon />;
      case 'pending':
        return <ClockIcon />;
      default:
        return <ExclamationCircleIcon />;
    }
  };

  if (selectedMarketplace) {
    const detailProducts = selectedMarketplace.products.filter((p) => {
      const matchesSearch = p.name
        .toLowerCase()
        .includes(detailSearch.toLowerCase());
      const matchesStatus =
        detailStatusFilter === 'all' || p.status === detailStatusFilter;
      return matchesSearch && matchesStatus;
    });

    return (
      <Container>
        <DetailHeader>
          <BackButton onClick={() => setSelectedMarketplace(null)}>
            <ArrowLeftIcon />
            Back to Marketplaces
          </BackButton>
          <DetailHeaderRow>
            <DetailMarketplaceLogo $color={selectedMarketplace.color}>
              <GlobeAltIcon />
            </DetailMarketplaceLogo>
            <DetailInfo>
              <DetailMarketplaceName>
                {selectedMarketplace.name}
              </DetailMarketplaceName>
              <DetailMarketplaceType>
                {selectedMarketplace.type} Marketplace · Last sync:{' '}
                {selectedMarketplace.lastSync ?? 'Never'}
              </DetailMarketplaceType>
            </DetailInfo>
            <StatusBadge $status={selectedMarketplace.status}>
              {getStatusIcon(selectedMarketplace.status)}
              {selectedMarketplace.status.charAt(0).toUpperCase() +
                selectedMarketplace.status.slice(1)}
            </StatusBadge>
            <SyncButton>
              <ArrowPathIcon />
              Sync Now
            </SyncButton>
            <ActionButton onClick={() => openConfigure(selectedMarketplace)}>
              <PencilIcon />
              Configure
            </ActionButton>
          </DetailHeaderRow>
        </DetailHeader>

        <DetailStatsBar>
          <DetailStatCard>
            <DetailStatLabel>Total Products</DetailStatLabel>
            <DetailStatValue>
              {selectedMarketplace.totalProducts}
            </DetailStatValue>
          </DetailStatCard>
          <DetailStatCard>
            <DetailStatLabel>Active Listings</DetailStatLabel>
            <DetailStatValue>
              {selectedMarketplace.activeListings}
            </DetailStatValue>
          </DetailStatCard>
          <DetailStatCard>
            <DetailStatLabel>Total Sales</DetailStatLabel>
            <DetailStatValue>
              {selectedMarketplace.totalSales.toLocaleString()}
            </DetailStatValue>
          </DetailStatCard>
          <DetailStatCard>
            <DetailStatLabel>Revenue</DetailStatLabel>
            <DetailStatValue>
              ${(selectedMarketplace.revenue / 1000).toFixed(0)}k
            </DetailStatValue>
          </DetailStatCard>
        </DetailStatsBar>

        <PublishedSection>
          <SectionHeader>
            <SectionTitle>
              Products on {selectedMarketplace.name} ({detailProducts.length})
            </SectionTitle>
            <TableFilters>
              <SearchBox>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder="Search products..."
                  value={detailSearch}
                  onChange={(e) => setDetailSearch(e.target.value)}
                />
              </SearchBox>
              <FilterSelect
                value={detailStatusFilter}
                onChange={(e) => setDetailStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="pending">Pending</option>
                <option value="error">Error</option>
                <option value="draft">Draft</option>
              </FilterSelect>
            </TableFilters>
          </SectionHeader>
          <TableWrapper>
            <Table>
              <Thead>
                <tr>
                  <Th>Product Name</Th>
                  <Th>SKU</Th>
                  <Th>Price</Th>
                  <Th>Sync Status</Th>
                  <Th>Last Synced</Th>
                  <Th></Th>
                </tr>
              </Thead>
              <Tbody>
                {detailProducts.length === 0 ? (
                  <EmptyTableRow>
                    <EmptyTableCell colSpan={6}>
                      No products match the selected filters.
                    </EmptyTableCell>
                  </EmptyTableRow>
                ) : (
                  detailProducts.map((product) => (
                    <Tr key={product.id}>
                      <Td>
                        <ProductNameCell>{product.name}</ProductNameCell>
                      </Td>
                      <Td>
                        <SkuCell>
                          {product.sku ?? `SKU-${product.id.padStart(4, '0')}`}
                        </SkuCell>
                      </Td>
                      <Td>
                        <ProductPriceCell>
                          ${product.price.toFixed(2)}
                        </ProductPriceCell>
                      </Td>
                      <Td>
                        <ProductStatusBadge $status={product.status}>
                          {product.status.charAt(0).toUpperCase() +
                            product.status.slice(1)}
                        </ProductStatusBadge>
                      </Td>
                      <Td>{selectedMarketplace.lastSync ?? '—'}</Td>
                      <Td style={{ display: 'flex', gap: '0.5rem' }}>
                        <SyncActionButton>
                          <ArrowPathIcon />
                          Sync
                        </SyncActionButton>
                        <RemoveButton>
                          <TrashIcon />
                          Remove
                        </RemoveButton>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableWrapper>
        </PublishedSection>

        {configuringMarketplace && (
          <ModalOverlay onClick={closeConfigure}>
            <ModalPanel onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  Configure — {configuringMarketplace.name}
                </ModalTitle>
                <ModalCloseButton onClick={closeConfigure}>
                  <XMarkIcon />
                </ModalCloseButton>
              </ModalHeader>
              <ModalBody>
                <FormRow>
                  <FormLabel>Display Name</FormLabel>
                  <FormInput
                    value={configForm.name}
                    onChange={(e) =>
                      setConfigForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>API Key</FormLabel>
                  <FormInput
                    type="password"
                    value={configForm.apiKey}
                    onChange={(e) =>
                      setConfigForm((f) => ({ ...f, apiKey: e.target.value }))
                    }
                    placeholder="Enter API key"
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>API Secret</FormLabel>
                  <FormInput
                    type="password"
                    value={configForm.apiSecret}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        apiSecret: e.target.value,
                      }))
                    }
                    placeholder="Enter API secret"
                  />
                </FormRow>
                {configuringMarketplace.type === 'Amazon' && (
                  <FormRow>
                    <FormLabel>Region</FormLabel>
                    <FormSelect
                      value={configForm.region}
                      onChange={(e) =>
                        setConfigForm((f) => ({ ...f, region: e.target.value }))
                      }
                    >
                      <option value="us-east-1">US East (N. Virginia)</option>
                      <option value="us-west-2">US West (Oregon)</option>
                      <option value="eu-west-1">EU (Ireland)</option>
                      <option value="eu-central-1">EU (Frankfurt)</option>
                      <option value="ap-southeast-1">
                        Asia Pacific (Singapore)
                      </option>
                    </FormSelect>
                  </FormRow>
                )}
                <FormRow>
                  <FormLabel>Sync Interval (minutes)</FormLabel>
                  <FormSelect
                    value={configForm.syncInterval}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        syncInterval: e.target.value,
                      }))
                    }
                  >
                    <option value="5">Every 5 minutes</option>
                    <option value="15">Every 15 minutes</option>
                    <option value="30">Every 30 minutes</option>
                    <option value="60">Every hour</option>
                    <option value="360">Every 6 hours</option>
                    <option value="1440">Once a day</option>
                  </FormSelect>
                </FormRow>
                <FormRow>
                  <FormToggleRow>
                    <div>
                      <FormToggleLabel>Auto Sync</FormToggleLabel>
                      <FormToggleDesc>
                        Automatically sync products on the selected interval
                      </FormToggleDesc>
                    </div>
                    <Toggle
                      type="checkbox"
                      role="switch"
                      checked={configForm.autoSync}
                      onChange={(e) =>
                        setConfigForm((f) => ({
                          ...f,
                          autoSync: e.target.checked,
                        }))
                      }
                    />
                  </FormToggleRow>
                </FormRow>
              </ModalBody>
              <ModalFooter>
                <ModalCancelButton onClick={closeConfigure}>
                  Cancel
                </ModalCancelButton>
                <ModalSaveButton onClick={closeConfigure}>
                  Save Changes
                </ModalSaveButton>
              </ModalFooter>
            </ModalPanel>
          </ModalOverlay>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Marketplaces</Title>
        <Controls>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search marketplaces..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          <FilterSelect
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
            <option value="inactive">Inactive</option>
          </FilterSelect>
          <AddButton>
            <PlusIcon />
            Connect Marketplace
          </AddButton>
        </Controls>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatLabel>Total Marketplaces</StatLabel>
            <StatIconWrapper $color="var(--primary-600)">
              <GlobeAltIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.totalMarketplaces}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Active Marketplaces</StatLabel>
            <StatIconWrapper $color="var(--success-600)">
              <CheckCircleIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.activeMarketplaces}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Total Listed Products</StatLabel>
            <StatIconWrapper $color="var(--blue-600)">
              <ShoppingBagIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{stats.totalProducts}</StatValue>
        </StatCard>
        <StatCard>
          <StatHeader>
            <StatLabel>Total Revenue</StatLabel>
            <StatIconWrapper $color="var(--purple-600)">
              <ChartBarIcon />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>${(stats.totalRevenue / 1000).toFixed(0)}k</StatValue>
        </StatCard>
      </StatsGrid>

      {filteredMarketplaces.length === 0 && connectedSlugs !== null ? (
        <EmptyState>
          <GlobeAltIcon
            style={{
              width: '3rem',
              height: '3rem',
              color: 'var(--gray-300)',
              marginBottom: '1rem',
            }}
          />
          <EmptyStateTitle>No connected marketplaces</EmptyStateTitle>
          <EmptyStateText>
            Connect a marketplace in Settings → Integrations to start managing
            your listings here.
          </EmptyStateText>
          <EmptyStateLink href="/settings#integrations">
            Go to Integrations
          </EmptyStateLink>
        </EmptyState>
      ) : (
        <MarketplacesGrid>
          {filteredMarketplaces.map((marketplace) => (
            <MarketplaceCard key={marketplace.id}>
              <MarketplaceHeader>
                <MarketplaceInfo>
                  <MarketplaceLogo $color={marketplace.color}>
                    <GlobeAltIcon />
                  </MarketplaceLogo>
                  <MarketplaceDetails>
                    <MarketplaceName>{marketplace.name}</MarketplaceName>
                    <MarketplaceType>
                      {marketplace.type} Marketplace
                    </MarketplaceType>
                  </MarketplaceDetails>
                </MarketplaceInfo>
                <MarketplaceActions>
                  <StatusBadge $status={marketplace.status}>
                    {getStatusIcon(marketplace.status)}
                    {marketplace.status.charAt(0).toUpperCase() +
                      marketplace.status.slice(1)}
                  </StatusBadge>
                  <SyncButton>
                    <ArrowPathIcon />
                    Sync Now
                  </SyncButton>
                  <ActionButton onClick={() => openConfigure(marketplace)}>
                    <PencilIcon />
                    Configure
                  </ActionButton>
                  <ViewButton
                    onClick={() => setSelectedMarketplace(marketplace)}
                  >
                    View
                  </ViewButton>
                </MarketplaceActions>
              </MarketplaceHeader>

              <MarketplaceStats>
                <Stat>
                  <StatTitle>Total Products</StatTitle>
                  <StatNumber>{marketplace.totalProducts}</StatNumber>
                </Stat>
                <Stat>
                  <StatTitle>Active Listings</StatTitle>
                  <StatNumber>{marketplace.activeListings}</StatNumber>
                </Stat>
                <Stat>
                  <StatTitle>Total Sales</StatTitle>
                  <StatNumber>
                    {marketplace.totalSales.toLocaleString()}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatTitle>Revenue</StatTitle>
                  <StatNumber>
                    ${(marketplace.revenue / 1000).toFixed(0)}k
                  </StatNumber>
                </Stat>
              </MarketplaceStats>
            </MarketplaceCard>
          ))}
        </MarketplacesGrid>
      )}

      <PublishedSection>
        <SectionHeader>
          <SectionTitle>
            Published Listings ({filteredProducts.length})
          </SectionTitle>
          <TableFilters>
            <SearchBox>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </SearchBox>
            <FilterSelect
              value={productMarketplaceFilter}
              onChange={(e) => setProductMarketplaceFilter(e.target.value)}
            >
              <option value="all">All Marketplaces</option>
              {marketplaces.map((mp) => (
                <option key={mp.id} value={mp.id}>
                  {mp.name}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={productStatusFilter}
              onChange={(e) => setProductStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="published">Published</option>
              <option value="pending">Pending</option>
              <option value="error">Error</option>
              <option value="draft">Draft</option>
            </FilterSelect>
          </TableFilters>
        </SectionHeader>
        <TableWrapper>
          <Table>
            <Thead>
              <tr>
                <Th>Product Name</Th>
                <Th>Price</Th>
                <Th>Marketplace</Th>
                <Th>Status</Th>
                <Th>Last Sync</Th>
                <Th></Th>
              </tr>
            </Thead>
            <Tbody>
              {filteredProducts.length === 0 ? (
                <EmptyTableRow>
                  <EmptyTableCell colSpan={6}>
                    No listings match the selected filters.
                  </EmptyTableCell>
                </EmptyTableRow>
              ) : (
                filteredProducts.map((product) => (
                  <Tr key={`${product.marketplaceId}-${product.id}`}>
                    <Td>
                      <ProductNameCell>{product.name}</ProductNameCell>
                    </Td>
                    <Td>
                      <ProductPriceCell>
                        ${product.price.toFixed(2)}
                      </ProductPriceCell>
                    </Td>
                    <Td>
                      <MarketplaceBadge $color={product.marketplaceColor}>
                        {product.marketplaceName}
                      </MarketplaceBadge>
                    </Td>
                    <Td>
                      <ProductStatusBadge $status={product.status}>
                        {product.status.charAt(0).toUpperCase() +
                          product.status.slice(1)}
                      </ProductStatusBadge>
                    </Td>
                    <Td>{product.lastSync ?? '—'}</Td>
                    <Td>
                      <RemoveButton>
                        <TrashIcon />
                        Remove
                      </RemoveButton>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableWrapper>
      </PublishedSection>

      {configuringMarketplace && (
        <ModalOverlay onClick={closeConfigure}>
          <ModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Configure — {configuringMarketplace.name}</ModalTitle>
              <ModalCloseButton onClick={closeConfigure}>
                <XMarkIcon />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <FormRow>
                <FormLabel>Display Name</FormLabel>
                <FormInput
                  value={configForm.name}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </FormRow>
              <FormRow>
                <FormLabel>API Key</FormLabel>
                <FormInput
                  type="password"
                  value={configForm.apiKey}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, apiKey: e.target.value }))
                  }
                  placeholder="Enter API key"
                />
              </FormRow>
              <FormRow>
                <FormLabel>API Secret</FormLabel>
                <FormInput
                  type="password"
                  value={configForm.apiSecret}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, apiSecret: e.target.value }))
                  }
                  placeholder="Enter API secret"
                />
              </FormRow>
              {configuringMarketplace.type === 'Amazon' && (
                <FormRow>
                  <FormLabel>Region</FormLabel>
                  <FormSelect
                    value={configForm.region}
                    onChange={(e) =>
                      setConfigForm((f) => ({ ...f, region: e.target.value }))
                    }
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="ap-southeast-1">
                      Asia Pacific (Singapore)
                    </option>
                  </FormSelect>
                </FormRow>
              )}
              <FormRow>
                <FormLabel>Sync Interval (minutes)</FormLabel>
                <FormSelect
                  value={configForm.syncInterval}
                  onChange={(e) =>
                    setConfigForm((f) => ({
                      ...f,
                      syncInterval: e.target.value,
                    }))
                  }
                >
                  <option value="5">Every 5 minutes</option>
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                  <option value="360">Every 6 hours</option>
                  <option value="1440">Once a day</option>
                </FormSelect>
              </FormRow>
              <FormRow>
                <FormToggleRow>
                  <div>
                    <FormToggleLabel>Auto Sync</FormToggleLabel>
                    <FormToggleDesc>
                      Automatically sync products on the selected interval
                    </FormToggleDesc>
                  </div>
                  <Toggle
                    type="checkbox"
                    role="switch"
                    checked={configForm.autoSync}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        autoSync: e.target.checked,
                      }))
                    }
                  />
                </FormToggleRow>
              </FormRow>
            </ModalBody>
            <ModalFooter>
              <ModalCancelButton onClick={closeConfigure}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton onClick={closeConfigure}>
                Save Changes
              </ModalSaveButton>
            </ModalFooter>
          </ModalPanel>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Marketplaces;
